/* ============================================================== */
/*                                                                */
/*   ResultSummaryV4 · sequence ของหน้า "Result Summary" (HIS V4)   */
/*                                                                */
/*   practice เดียวกับ ExamV4 / PatientOrdersV4 / MultidisciplinaryV4: */
/*     data    -> state.ResultSummaryV4Sequence                       */
/*     loading -> state.buttonLoadCheck["CardResultSummaryV4_<ACTION>"] */
/*     error   -> state.errorMessage["CardResultSummaryV4"]           */
/*     card เรียก props.runSequence({ sequence, action, card, ... })  */
/*     (card ห้าม import issara-sdk — ดู architectureV4.test.ts)      */
/*                                                                */
/*   HIS V3 ที่อ้างอิง:                                              */
/*     react-lib/apps/HISV3/LAB/sequence/HistoryCentralLab.ts:57-96  */
/*       CentralLabOrderList.list({patient|encounter}) ->             */
/*       ผลแลป: GET central-lab-result-by-encounter/?encounter= (call เดียวทั้ง encounter)  */
/*     react-lib/apps/HISV3/IME/IMEInterface.ts                       */
/*       GET /apis/IME/list_result/?encounter=                        */
/*     react-lib/apps/HISV3/LAB/sequence/OPDLabSummary.ts:521-560    */
/*       POST /apis/LAB/central-lab-result-comparable/ (graph ย้อนหลัง) */
/*                                                                */
/* ============================================================== */

import WasmController from "react-lib/frameworks/WasmController";

import CentralLabOrderList from "issara-sdk/apis/CentralLabOrderList_apps_LAB";
import CentralLabResultEncounterList from "issara-sdk/apis/CentralLabResultEncounterList_apps_LAB";
import CentralLabResultComparable from "issara-sdk/apis/CentralLabResultComparable_apps_LAB";
import ImagingOrderResultList from "issara-sdk/apis/ImagingOrderResultList_apps_IME";
import SpecimenContainerEndCollectSpecimen from "issara-sdk/apis/SpecimenContainerEndCollectSpecimen_apps_LAB";
import SpecimenContainerPrintSticker from "issara-sdk/apis/SpecimenContainerPrintSticker_apps_LAB";
import CentralLabResultCreateList from "issara-sdk/apis/CentralLabResultCreateList_apps_LAB";
import CheckPermissionLabResultConfidential from "issara-sdk/apis/CheckPermissionLabResultConfidential_apps_LAB";
import ProcedureCreate from "issara-sdk/apis/ProcedureCreate_apps_DPI";
import DoctorFeeOrderCreateUpdateList from "issara-sdk/apis/DoctorFeeOrderCreateUpdateList_apps_DFC";
import MedicationAdminCreate from "issara-sdk/apis/MedicationAdminCreate_apps_TPD";
import MedicationRecordListSummaryView from "issara-sdk/apis/MedicationRecordListSummaryView_apps_TPD";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  apiErrorTextOf,
  markButtonLoadCheck,
  seqBtnKey,
  seqErrorKey,
  writeSequenceState,
  type BLC_STATUS_TYPE,
  type SeqMeta,
} from "./sequenceStateV4";

import {
ResultIds,
  ResultV4Slice,
  ProcedureRow,
  DrugOrderRow,
  comparablePayload,
  drugOrdersFromApi,
  emptyResultV4Slice,
  flattenLabResults,
  imagingResultParams,
  imagingResultsFromApi,
  labOrderListParams,
  labOrdersFromApi,
  labResultsFromApi,
  normalizeResultV4Slice,
  proceduresFromApi,
  specimensFromOrderPayload,
  trendFromComparable,
  marDateText,
} from "../../patient/resultModelV4";
import { asNumber, type Dict } from "../../patient/examModelV4";

/* ============================================================== */
/*   SECTION 1 · TYPE-DEFINING CONSTANTS                           */
/* ============================================================== */

export const SEQUENCE_NAME = "ResultSummaryV4" as const;
export const CARD_NAME = "CardResultSummaryV4";

export const ACTIONS = {
  LOAD: "LOAD",
  LOAD_PATIENT: "LOAD_PATIENT",
  LOAD_TREND: "LOAD_TREND",
  COLLECT_SPECIMEN: "COLLECT_SPECIMEN",
  PRINT_SPECIMEN_STICKER: "PRINT_SPECIMEN_STICKER",
  SAVE_LAB_RESULT: "SAVE_LAB_RESULT",
  CHECK_CONFIDENTIAL: "CHECK_CONFIDENTIAL",
  LOCK_CONFIDENTIAL: "LOCK_CONFIDENTIAL",
  SAVE_PROCEDURE: "SAVE_PROCEDURE",
  CLAIM_DOCTOR_FEE: "CLAIM_DOCTOR_FEE",
  SAVE_MED_ADMIN: "SAVE_MED_ADMIN",
} as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const RESULT_SLICE_KEY = "ResultSummaryV4Sequence";

/* ============================================================== */
/*   SECTION 2 · STATE                                             */
/* ============================================================== */

export type State = Partial<{
  ResultSummaryV4Sequence: Partial<ResultV4Slice> | null;
}>;

export const StateInitial: State = { ResultSummaryV4Sequence: null };

export type Event = { message: "RunSequence"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export const DataInitial: Data = {};

export type Props = {};

/* ============================================================== */
/*   SECTION 3 · PARAMS TYPE                                       */
/* ============================================================== */

type SequenceName = typeof SEQUENCE_NAME;

type Actions = typeof ACTIONS;
export type ActionName = Actions[keyof Actions];

export type ResultIdsInput = Partial<ResultIds> & { ids?: ResultIds };

export type SeqState = BaseSeqState<SequenceName> &
  ResultIdsInput & {
    card?: string;
    encounter?: { id?: unknown };
  };

export type ActionPayloadMap = {
  [ACTIONS.LOAD]: ResultIdsInput;
  [ACTIONS.LOAD_PATIENT]: ResultIdsInput;
  [ACTIONS.LOAD_TREND]: ResultIdsInput & { labId: number };
  [ACTIONS.COLLECT_SPECIMEN]: ResultIdsInput & {
    username?: string;
    password?: string;
    specimenContainers: number[];
    isBlood?: boolean;
  };
  [ACTIONS.PRINT_SPECIMEN_STICKER]: ResultIdsInput & {
    containerId: number;
    isRobo?: boolean;
  };
  [ACTIONS.SAVE_LAB_RESULT]: ResultIdsInput & {
    orderItemId: number;
    resultId?: number;
    value: string;
    comment?: string;
    username?: string;
    password?: string;
  };
  [ACTIONS.CHECK_CONFIDENTIAL]: ResultIdsInput & {
    username?: string;
    password?: string;
  };
  [ACTIONS.LOCK_CONFIDENTIAL]: ResultIdsInput;
  [ACTIONS.SAVE_PROCEDURE]: ResultIdsInput & {
    icd9cm?: number;
    procedureName?: string;
    practitionerId?: number;
    result?: string;
    complication?: string;
  };
  [ACTIONS.CLAIM_DOCTOR_FEE]: ResultIdsInput & {
    procedureId?: number;
    amount?: number;
    doctorId?: number;
  };
  [ACTIONS.SAVE_MED_ADMIN]: ResultIdsInput & {
    medicationRecordId?: number;
    drugOrderItemId?: number;
    administeredDatetime?: string;
    site?: string;
    lotNumber?: string;
    rate?: string;
    observation?: string;
  };
};

type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];
export type ResultRunSequence = RunSequence;

export type ResultRunParams = {
  [K in ActionName]: {
    sequence: typeof SEQUENCE_NAME;
    action: K;
    card?: string;
    restart?: boolean;
    clear?: boolean;
  } & ActionPayloadMap[K];
}[ActionName];

/** params แบบกว้างที่ card ประกอบ (payload บาง action TS ตรวจไม่ครบเพราะ spread) */
export type ResultRunParamsLoose = {
  sequence: string;
  action: string;
  card?: string;
  restart?: boolean;
  clear?: boolean;
  [key: string]: unknown;
};

export type ResultRunSequenceProp = (params: ResultRunParams | ResultRunParamsLoose) => void;

export type SetProp = ActionTypes["SetProp"];
export type OnEvent = ActionTypes["OnEvent"];

type Ctx<A extends ActionName> = ResultIdsInput & {
  ids: ResultIds;
  card?: string;
  action?: A;
  labId?: number;
  /** กรอบวันที่ของ "ผลสะสม" (DD/MM/YYYY พ.ศ.) — backend คำนวณให้ถ้าไม่ส่ง */
  fromDate?: string;
  toDate?: string;
  username?: string;
  password?: string;
  specimenContainers?: number[];
  isBlood?: boolean;
  containerId?: number;
  isRobo?: boolean;
  /** SAVE_LAB_RESULT: pk ของ order item + id ของ result เดิม (null ถ้ายังไม่เคยลงผล) */
  orderItemId?: number;
  resultId?: number;
  value?: string;
  comment?: string;
  icd9cm?: number;
  procedureName?: string;
  practitionerId?: number;
  result?: string;
  complication?: string;
  procedureId?: number;
  amount?: number;
  doctorId?: number;
  medicationRecordId?: number;
  drugOrderItemId?: number;
  administeredDatetime?: string;
  site?: string;
  lotNumber?: string;
  rate?: string;
  observation?: string;
};

type CombinedState = PickedState & State;

export type Handler<P = unknown, R = void> = ActionHandler<CombinedState, Event, Data, P, R>;

type Ctl = {
  getState: () => CombinedState & Record<string, any>;
  setState: (state: any, callback?: () => void) => any;
  handleEvent: (event: Event) => void;
  apiToken?: string;
  data?: Data;
};

/* ============================================================== */
/*   SECTION 4 · INTERNAL HELPERS                                  */
/* ============================================================== */

type ApiTuple = [unknown, unknown, unknown];

const extraOf = (controller: Ctl) => ({
  division: controller.data?.division,
  device: controller.data?.device,
});

const errorMessageOf = (result: ApiTuple): string | null =>
  apiErrorTextOf(result, { fallbackText: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" });

const dataOf = (result: ApiTuple): unknown => result[0];

const sliceOf = (controller: Ctl): ResultV4Slice =>
  normalizeResultV4Slice(controller.getState().ResultSummaryV4Sequence);

type SliceMeta = { card?: string; action?: string };

const metaOf = (meta: SliceMeta): SeqMeta => ({
  sequence: SEQUENCE_NAME,
  card: meta.card ?? CARD_NAME,
  action: meta.action,
});

export const resultBtnKey = (meta: SliceMeta): string => seqBtnKey(metaOf(meta));
export const resultErrorKey = (meta: SliceMeta): string => seqErrorKey(metaOf(meta));

type WritePatch = Partial<ResultV4Slice> & {
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
};

const writeSlice = (controller: Ctl, meta: SliceMeta, patch: WritePatch): any =>
  writeSequenceState<ResultV4Slice>(
    controller,
    metaOf(meta),
    RESULT_SLICE_KEY,
    sliceOf(controller),
    { ...patch, sequenceIndex: "Action" },
    emptyResultV4Slice,
  );

const setBtn = (controller: Ctl, metas: SliceMeta[], status: BLC_STATUS_TYPE): void =>
  markButtonLoadCheck(controller, metas.map(metaOf), status);

/** เขียน error เข้า state.errorMessage[card] แล้วตั้ง buttonLoadCheck = ERROR */
const reportError = (controller: Ctl, meta: SliceMeta, message: string): void =>
  writeSlice(controller, meta, { error: message });

const numberOrUndefined = (value: unknown): number | undefined => asNumber(value);

const textOrEmpty = (value: unknown): string => (value === undefined || value === null ? "" : String(value));

/** ids: params -> slice -> state shared ของ MainHIS (selectedEncounter/selectedEmr/patient) */
const readIds = (controller: Ctl, params: ResultIdsInput): ResultIds => {
  const slice = sliceOf(controller);
  const app = controller.getState() as {
    selectedEncounter?: { id?: unknown; patient_id?: unknown };
    selectedEmr?: { id?: unknown; patient?: unknown };
    patient?: { id?: unknown };
  };
  const fromParams: Partial<ResultIds> = params.ids ?? params;

  return {
    encounterId:
      fromParams.encounterId ??
      slice.ids.encounterId ??
      numberOrUndefined(app.selectedEncounter?.id),
    emrId: fromParams.emrId ?? slice.ids.emrId ?? numberOrUndefined(app.selectedEmr?.id),
    patientId:
      fromParams.patientId ??
      slice.ids.patientId ??
      numberOrUndefined(app.selectedEncounter?.patient_id) ??
      numberOrUndefined(app.patient?.id),
  };
};

/** รวม error หลาย source ให้เป็นข้อความเดียว (endpoint ไหนล้มก็ยังไม่เงียบ) */
const joinErrors = (errors: (string | null)[]): string | null => {
  const texts = errors.filter((text): text is string => text !== null && text !== "");
  return texts.length === 0 ? null : texts.join(" · ");
};

/* ============================================================== */
/*   SECTION 5 · LOAD                                              */
/* ============================================================== */

/**
 * อ่านแลปของ encounter:
 *   1) GET /apis/LAB/central-lab-order/?encounter=  (ออเดอร์ + order_items + specimen_containers)
 *   2) ต่อออเดอร์: GET /apis/LAB/central-lab-order-item/result/?order_id=
 *      (staging verify แล้ว: ได้ value/result_id/report_by children ครบ)
 * และผล imaging: GET /apis/IME/list_result/?encounter=
 */
const loadLabOrders = async (
  controller: Ctl,
  ids: ResultIds,
  params: { encounterOnly: boolean },
): Promise<{ orders: ResultV4Slice["labOrders"]; specimens: ResultV4Slice["specimens"]; error: string | null }> => {
  const query = params.encounterOnly ? labOrderListParams(ids) : { patient: ids.patientId };
  if (Object.keys(query).length === 0) {
    return { orders: [], specimens: [], error: "ยังไม่ได้เลือกผู้ป่วย (encounter/patient)" };
  }

  const result = await CentralLabOrderList.list({
    params: query,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);

  return {
    orders: error ? [] : labOrdersFromApi(dataOf(result)),
    specimens: error ? [] : specimensFromOrderPayload(dataOf(result)),
    error,
  };
};

/**
 * GET /apis/LAB/central-lab-result-by-encounter/?encounter=<id> — ผลแลปทั้งหมดของ encounter ใน call เดียว
 * (endpoint เดียวกับ PatientHistoryV4.ts:558 — เปลี่ยนจาก central-lab-order-item/result ต่อออเดอร์)
 * ค่าผลแต่ละแถว: value || latest_value (ดู labResultRowFromApi ใน resultModelV4)
 */
const loadLabResults = async (
  controller: Ctl,
  ids: ResultIds,
): Promise<{ rows: ResultV4Slice["labResults"]; error: string | null }> => {
  if (ids.encounterId === undefined) return { rows: [], error: null };

  const result = await CentralLabResultEncounterList.list({
    params: { encounter: ids.encounterId },
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);

  return {
    rows: error === null ? flattenLabResults([labResultsFromApi(dataOf(result))]) : [],
    error,
  };
};

const loadImaging = async (
  controller: Ctl,
  ids: ResultIds,
): Promise<{ rows: ResultV4Slice["imagingResults"]; error: string | null }> => {
  if (ids.encounterId === undefined) return { rows: [], error: null };

  const result = await ImagingOrderResultList.list({
    params: imagingResultParams(ids),
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);

  return { rows: error ? [] : imagingResultsFromApi(dataOf(result)), error };
};

const LoadResults: Handler<Ctx<"LOAD">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const { orders, specimens, error: orderError } = await loadLabOrders(controller, ids, {
    encounterOnly: true,
  });

  if (orderError !== null && orders.length === 0 && specimens.length === 0) {
    const imaging = await loadImaging(controller, ids);
    writeSlice(controller, params, {
      ids,
      labOrders: [],
      specimens: [],
      labResults: [],
      imagingResults: imaging.rows,
      error: joinErrors([orderError, imaging.error]),
    });
    return;
  }

  const [lab, imaging] = await Promise.all([
    loadLabResults(controller, ids),
    loadImaging(controller, ids),
  ]);

  /* TPD/medication-record-list-summary/ contract (checked against staging):
     - `emr` (MedicalRecord id) is required: with `encounter` only, the view keeps a str and crashes
       (encounter.actions -> AttributeError, HTTP 500)
     - `date` must be Buddhist era dd/mm/yyyy and non-empty, otherwise create_number_date does
       None += timedelta (TypeError, HTTP 500). The day used here is the day the lab order was ordered. */
  const medDate = orders.length > 0 ? marDateText(orders[0].orderedAtIso || orders[0].orderedAt) : "";
  const medRes = ids.emrId !== undefined && medDate !== ""
    ? await MedicationRecordListSummaryView.get({
        params: { emr: ids.emrId, encounter: ids.encounterId, date: medDate },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      })
    : [null, null];
  /* this endpoint answers HTTP 500 when it is called without a Buddhist-era `date` (its view builds 7
     day-columns). The result summary must still render lab/imaging rows and only report the failed part. */
  const medError = errorMessageOf(medRes);
  const drugOrders = drugOrdersFromApi(dataOf(medRes));

  writeSlice(controller, params, {
    ids,
    labOrders: orders,
    specimens,
    labResults: lab.rows,
    imagingResults: imaging.rows,
    drugOrders,
    error: joinErrors([orderError, lab.error, imaging.error, medError]),
  });
};

/** เหมือน LOAD แต่ใช้รายการแลปทั้งหมดของ patient (HistoryCentralLab.ts:57-61 ใช้ params {patient}) */
const LoadPatientHistory: Handler<Ctx<"LOAD_PATIENT">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const { orders, specimens, error } = await loadLabOrders(controller, ids, { encounterOnly: false });

  if (error !== null) {
    writeSlice(controller, params, { ids, labOrders: [], specimens: [], labResults: [], error });
    return;
  }

  const lab = await loadLabResults(controller, ids);

  writeSlice(controller, params, {
    ids,
    labOrders: orders,
    specimens,
    labResults: lab.rows,
    error: lab.error,
  });
};

/**
 * POST /apis/LAB/central-lab-result-comparable/ body {columns:[], items:[], patient, products, from_date}
 * ใช้กับ modal "ผลสะสม <ชื่อทดสอบ>" (design แสดง graph + ตารางย้อนหลัง)
 */
const LoadTrend: Handler<Ctx<"LOAD_TREND">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const labId = numberOrUndefined(params.labId);

  if (labId === undefined) {
    writeSlice(controller, params, { ids, trend: null, trendLabId: null, error: "ไม่พบรหัสทดสอบที่ต้องดูผลสะสม" });
    return;
  }
  if (ids.patientId === undefined) {
    writeSlice(controller, params, { ids, trend: null, trendLabId: labId, error: "ยังไม่มี patient สำหรับดูผลสะสม" });
    return;
  }

  /* ล้างผลสะสมของรายการก่อนหน้าทันที */
  writeSlice(controller, params, { ids, trend: null, trendLabId: labId, loading: true });

  const data: Dict = comparablePayload({
    patientId: ids.patientId,
    productIds: [labId],
    fromDate: params.fromDate,
    toDate: params.toDate,
  });

  const result = await CentralLabResultComparable.post({
    apiToken: controller.apiToken,
    data,
    extra: extraOf(controller),
  });
  const error = errorMessageOf(result);

  writeSlice(controller, params, {
    ids,
    trendLabId: labId,
    trend: error ? null : trendFromComparable(dataOf(result), labId),
    error,
  });
};

/**
 * POST /apis/LAB/specimen-container-end-collect-specimen/
 * เก็บสิ่งส่งตรวจ พร้อมยืนยันสิทธิ์ username / password
 */
const CollectSpecimen: Handler<Ctx<"COLLECT_SPECIMEN">> = async (controller, params) => {
  const ids = params.ids;
  const card = params.card ?? CARD_NAME;
  const meta: SliceMeta = { card, action: ACTIONS.COLLECT_SPECIMEN };
  const containers = params.specimenContainers || [];

  if (containers.length === 0) {
    return reportError(controller, meta, "กรุณาเลือกสิ่งส่งตรวจที่ต้องการเก็บ");
  }

  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const result = await SpecimenContainerEndCollectSpecimen.post({
    data: {
      username: params.username,
      password: params.password,
      is_blood: params.isBlood ?? true,
      specimen_containers: containers,
      auto_ack: false,
    },
    extra: extraOf(controller),
    apiToken: controller.apiToken,
  });

  const error = errorMessageOf(result);
  if (error) {
    setBtn(controller, [meta], BLC_STATUS.ERROR);
    return reportError(controller, meta, error);
  }

  const lab = await loadLabOrders(controller, ids, { encounterOnly: true });
  writeSlice(controller, meta, {
    ids,
    labOrders: lab.orders,
    specimens: lab.specimens,
    error: null,
  });

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * PATCH /apis/LAB/specimen-container-print-sticker/{pk}/
 * บันทึกการพิมพ์สติกเกอร์บาร์โค้ดสิ่งส่งตรวจ
 */
const PrintSpecimenSticker: Handler<Ctx<"PRINT_SPECIMEN_STICKER">> = async (controller, params) => {
  const ids = params.ids;
  const card = params.card ?? CARD_NAME;
  const meta: SliceMeta = { card, action: ACTIONS.PRINT_SPECIMEN_STICKER };
  const containerId = params.containerId;

  if (!containerId) {
    return reportError(controller, meta, "ไม่พบรหัสสิ่งส่งตรวจสำหรับพิมพ์สติกเกอร์");
  }

  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const result = await SpecimenContainerPrintSticker.patch({
    pk: containerId,
    data: { is_robo: params.isRobo ?? false },
    extra: extraOf(controller),
    apiToken: controller.apiToken,
  });

  const error = errorMessageOf(result);
  if (error) {
    setBtn(controller, [meta], BLC_STATUS.ERROR);
    return reportError(controller, meta, error);
  }

  /* โหลดออเดอร์/สิ่งส่งตรวจใหม่หลังพิมพ์สติกเกอร์สำเร็จ (status_name ของ container จะเปลี่ยนเป็นพิมพ์แล้ว) */
  const lab = await loadLabOrders(controller, ids, { encounterOnly: true });
  writeSlice(controller, meta, {
    ids,
    labOrders: lab.orders,
    specimens: lab.specimens,
    error: null,
  });

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * POST /apis/LAB/central-lab-result-create-list/
 * ลงผลแลปทีละรายการ (โหมดเดียวกับ V3 LabInterface.ts:837-860 SaveLabResult:
 * items[{id: result_id, order_item, value, comment}] + action "EDIT")
 * หลังบันทึกสำเร็จ reload labs+results ทั้ง encounter เพื่อให้ตารางได้ค่าจริงจาก API
 */
const SaveLabResult: Handler<Ctx<"SAVE_LAB_RESULT">> = async (controller, params) => {
  const ids = params.ids;
  const card = params.card ?? CARD_NAME;
  const meta: SliceMeta = { card, action: ACTIONS.SAVE_LAB_RESULT };

  if (!params.orderItemId) {
    return reportError(controller, meta, "ไม่พบ order item ของผลแลปที่ต้องการลงผล");
  }

  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const result = await CentralLabResultCreateList.post({
    data: {
      items: [
        {
          id: params.resultId ?? null,
          order_item: params.orderItemId,
          value: params.value,
          comment: params.comment ?? "",
        },
      ],
      action: "EDIT",
      use_current_user: true
    } as never,
    extra: extraOf(controller),
    apiToken: controller.apiToken,
  });

  const error = errorMessageOf(result);
  if (error !== null) {
    setBtn(controller, [meta], BLC_STATUS.ERROR);
    return reportError(controller, meta, error);
  }

  const lab = await loadLabOrders(controller, ids, { encounterOnly: true });
  const results = await loadLabResults(controller, ids);
  writeSlice(controller, meta, {
    ids,
    labOrders: lab.orders,
    specimens: lab.specimens,
    labResults: results.rows,
    error: null,
  });

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * POST /apis/LAB/check-permission-lab-result-confidential/
 * ตรวจสอบสิทธิ์การปลดล็อกผลแล็บที่เป็นความลับ
 */
const CheckConfidential: Handler<Ctx<"CHECK_CONFIDENTIAL">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const card = params.card ?? CARD_NAME;
  const meta: SliceMeta = { card, action: ACTIONS.CHECK_CONFIDENTIAL };

  /* username ของ user ที่ login อยู่: params -> userProfile -> currentDoctor (กัน parent ลืมส่ง) */
  const appState = controller.getState() as {
    userProfile?: Record<string, any>;
    currentDoctor?: Record<string, any>;
  };
  const username =
    params.username ||
    textOrEmpty(appState.userProfile?.username) ||
    textOrEmpty(appState.currentDoctor?.username) ||
    undefined;

  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const result = await CheckPermissionLabResultConfidential.post({
    data: {
      username,
      password: params.password,
    },
    extra: extraOf(controller),
    apiToken: controller.apiToken,
  });

  const error = errorMessageOf(result);
  if (error) {
    setBtn(controller, [meta], BLC_STATUS.ERROR);
    return reportError(controller, meta, error);
  }

  writeSlice(controller, meta, {
    ids,
    confidentialUnlocked: true,
    error: null,
  });

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * ล็อกผลลับอีกครั้ง: เขียน confidentialUnlocked=false กลับเข้า slice
 * จำเป็นเพราะ flag เป็น source of truth — ถ้าใช้แค่ local state ฝั่ง card
 * พอล็อกแล้วกดปลดล็อกใหม่ ค่าใน slice ยัง true เดิม effect จะไม่ทำงาน (ค่าไม่เปลี่ยน)
 */
const LockConfidential: Handler<Ctx<"LOCK_CONFIDENTIAL">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const card = params.card ?? CARD_NAME;
  const meta: SliceMeta = { card, action: ACTIONS.LOCK_CONFIDENTIAL };

  writeSlice(controller, meta, { ids, confidentialUnlocked: false, error: null });

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * POST /apis/DPI/procedure/
 * บันทึกผลการทำหัตถการ
 */
const SaveProcedure: Handler<Ctx<"SAVE_PROCEDURE">> = async (controller, params) => {
  const ids = params.ids;
  const card = params.card ?? CARD_NAME;
  const meta: SliceMeta = { card, action: ACTIONS.SAVE_PROCEDURE };

  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const result = await ProcedureCreate.create({
    data: {
      encounter: ids.encounterId,
      emr: ids.emrId,
      practitioner: params.practitionerId,
      icd9cm: params.icd9cm,
      procedure_name: params.procedureName,
      result: params.result,
      complication: params.complication,
    } as never,
    extra: extraOf(controller),
    apiToken: controller.apiToken,
  });

  const error = errorMessageOf(result);
  if (error) {
    setBtn(controller, [meta], BLC_STATUS.ERROR);
    return reportError(controller, meta, error);
  }

  const slice = sliceOf(controller);
  const createdRow = dataOf(result) as Dict;
  const newRow: ProcedureRow = {
    id: asNumber(createdRow?.id) ?? Date.now(),
    name: params.procedureName || "หัตถการ",
    icd9cm: String(params.icd9cm || ""),
    practitionerName: "",
    result: params.result || "",
    complication: params.complication || "ไม่มี",
    status: "เสร็จสิ้น",
    createdAt: new Date().toISOString(),
  };

  writeSlice(controller, meta, {
    ids,
    procedures: [newRow, ...slice.procedures],
    error: null,
  });

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * POST /apis/DFC/doctor-fee-order/
 * ขอเบิกค่าตอบแทนแพทย์ (Doctor Fee / DF)
 */
const ClaimDoctorFee: Handler<Ctx<"CLAIM_DOCTOR_FEE">> = async (controller, params) => {
  const card = params.card ?? CARD_NAME;
  const ids = params.ids;
  const meta: SliceMeta = { card, action: ACTIONS.CLAIM_DOCTOR_FEE };

  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const result = await DoctorFeeOrderCreateUpdateList.create({
    data: {
      encounter: ids.encounterId,
      doctor: params.doctorId,
      amount: params.amount,
      procedure: params.procedureId,
    } as never,
    extra: extraOf(controller),
    apiToken: controller.apiToken,
  });

  const error = errorMessageOf(result);
  if (error) {
    setBtn(controller, [meta], BLC_STATUS.ERROR);
    return reportError(controller, meta, error);
  }

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/**
 * POST /apis/TPD/medication-admin/
 * บันทึกการบริหารยา (MAR)
 */
const SaveMedAdmin: Handler<Ctx<"SAVE_MED_ADMIN">> = async (controller, params) => {
  const card = params.card ?? CARD_NAME;
  const meta: SliceMeta = { card, action: ACTIONS.SAVE_MED_ADMIN };

  setBtn(controller, [meta], BLC_STATUS.LOADING);

  const result = await MedicationAdminCreate.create({
    data: {
      medication_record: params.medicationRecordId,
      drug_order_item: params.drugOrderItemId,
      administered_datetime: params.administeredDatetime || new Date().toISOString(),
      site: params.site,
      lot_number: params.lotNumber,
      rate: params.rate,
      observation: params.observation,
    } as never,
    extra: extraOf(controller),
    apiToken: controller.apiToken,
  });

  const error = errorMessageOf(result);
  if (error) {
    setBtn(controller, [meta], BLC_STATUS.ERROR);
    return reportError(controller, meta, error);
  }

  return setBtn(controller, [meta], BLC_STATUS.SUCCESS);
};

/* ============================================================== */
/*   SECTION 6 · ENTRY POINTS                                      */
/* ============================================================== */

export const Start: Handler<SeqState> = async (controller, params) => {
  const ids = readIds(controller, params);
  const card = (params as { card?: string }).card ?? CARD_NAME;

  controller.setState({
    ResultSummaryV4Sequence: { ...emptyResultV4Slice(), ids, sequenceIndex: "Action" },
  });
  setBtn(controller, [{ card, action: ACTIONS.LOAD }], BLC_STATUS.LOADING);

  if (!ids.encounterId) {
    return reportError(controller, { card, action: ACTIONS.LOAD }, "ยังไม่ได้เลือกผู้ป่วย (encounter)");
  }

  await LoadResults(controller, { ...params, ids, card, action: ACTIONS.LOAD });

  return setBtn(controller, [{ card, action: ACTIONS.LOAD }], BLC_STATUS.SUCCESS);
};

export const Action: Handler<ActionType> = async (controller, params) => {
  const action = params.action as ActionName;
  /* ActionType ไม่มี field card (card มาจาก params ของ card ผู้ส่ง) จึงอ่านผ่าน meta cast */
  const meta: SliceMeta = { card: (params as SliceMeta).card, action };

  const actionHandlers = {
    [ACTIONS.LOAD]: LoadResults,
    [ACTIONS.LOAD_PATIENT]: LoadPatientHistory,
    [ACTIONS.LOAD_TREND]: LoadTrend,
    [ACTIONS.COLLECT_SPECIMEN]: CollectSpecimen,
    [ACTIONS.PRINT_SPECIMEN_STICKER]: PrintSpecimenSticker,
    [ACTIONS.SAVE_LAB_RESULT]: SaveLabResult,
    [ACTIONS.CHECK_CONFIDENTIAL]: CheckConfidential,
    [ACTIONS.LOCK_CONFIDENTIAL]: LockConfidential,
    [ACTIONS.SAVE_PROCEDURE]: SaveProcedure,
    [ACTIONS.CLAIM_DOCTOR_FEE]: ClaimDoctorFee,
    [ACTIONS.SAVE_MED_ADMIN]: SaveMedAdmin,
  };

  const handler = actionHandlers[action] as Handler<Ctx<ActionName>> | undefined;

  if (handler) {
    return handler(controller, { ...params, action, ids: readIds(controller, params) });
  }

  console.error(`No handler found for action: ${action}`);

  return reportError(controller, meta, `ไม่รู้จัก action "${action}" ใน ${SEQUENCE_NAME}`);
};

